import dayjs from "dayjs";

import {
  buildPetRef,
  normalizeName,
  resolveMessage,
} from "../functions";

export const CRM_PET_RULES = {
  ABSENT: "ABSENT",
  NO_PRODUCT: "NO_PRODUCT",
  NO_ADDITIONAL: "NO_ADDITIONAL",
  POTENTIAL_SUBSCRIBER: "POTENTIAL_SUBSCRIBER",
};

export const CRM_PET_RULE_OPTIONS = [
  { value: CRM_PET_RULES.ABSENT, label: "Ausentes" },
  { value: CRM_PET_RULES.NO_PRODUCT, label: "Sem produtos" },
  { value: CRM_PET_RULES.NO_ADDITIONAL, label: "Sem adicionais" },
  { value: CRM_PET_RULES.POTENTIAL_SUBSCRIBER, label: "Possíveis assinantes" },
];

const LEGACY_NO_ADDITIONAL_INDIVIDUAL_TEMPLATE = "Oi, {nome_cliente}! 😊\n\nQue tal complementar os cuidados de {nome_pet}? Temos opções para deixar essa fofura ainda mais bem cuidada. 🐾🧡";

const NO_ADDITIONAL_INDIVIDUAL_TEMPLATE =
  "🐾 Olá, {nome_cliente}! Tudo bem?\n" +
  "Aproveitando o banho {artigo_pet} {nome_pet}, que tal incluir um cuidado extra para deixar esse momento ainda mais completo?\n\n" +
  "✨ *Hidratação* – ajuda a manter a pelagem macia e bem cuidada\n" +
  "🦷 *Escovação dentária* – auxilia na higiene bucal e na redução do acúmulo de placa bacteriana\n" +
  "🐾 *Corte de unhas* – ajuda a manter as unhas no tamanho adequado, trazendo mais conforto ao caminhar\n\n" +
  "Quer aproveitar o banho de hoje e adicionar algum desses cuidados para {nome_pet}? 😊";

export const CRM_PET_DEFAULT_MESSAGES = {
  [CRM_PET_RULES.ABSENT]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\nJá faz {dias_ausente} dias que não vemos {nome_pet} por aqui. Estamos com saudades! 🐾🧡",
    consolidatedFragment: "• Estamos com saudades de {nome_pet}; já faz {dias_ausente} dias desde a última visita.",
  },
  [CRM_PET_RULES.NO_PRODUCT]: {
    individualTemplate:
      "🐾 Hora de repor os cuidados do seu pet!\n" +
      "Alguns itens fazem parte da rotina e não podem faltar 💛\n" +
      "Aproveite para conferir se está tudo em dia por aí:\n\n" +
      "✔ Ração\n" +
      "✔ Tapete higiênico\n" +
      "✔ Petiscos\n" +
      "✔ Antipulgas\n\n" +
      "Se algum deles estiver acabando, já sabe: passe aqui na Petland e garanta tudo o que seu pet precisa em um só lugar. 🛍️🐶🐱\n" +
      "Esperamos você!",
    consolidatedFragment: "• Confira se está na hora de repor ração, tapete higiênico, petiscos ou antipulgas.",
  },
  [CRM_PET_RULES.NO_ADDITIONAL]: {
    individualTemplate: NO_ADDITIONAL_INDIVIDUAL_TEMPLATE,
    consolidatedFragment: "• Temos opções para complementar os cuidados de {nome_pet}.",
  },
  [CRM_PET_RULES.POTENTIAL_SUBSCRIBER]: {
    individualTemplate:
      "Você sabia que manter uma rotina de banhos também é uma forma de cuidar da saúde do seu pet? 🐶💦\n" +
      "Com a Assinatura de Banhos Petland, fica muito mais fácil manter os cuidados sempre em dia:\n\n" +
      "🐾 Ajuda a manter a pele e a pelagem bem cuidadas\n" +
      "🐾 Evita o acúmulo de sujeira e odores\n" +
      "🐾 Garante uma rotina de mais cuidado, conforto e bem-estar\n\n" +
      "Além da economia, assinantes contam com descontos exclusivos em produtos selecionados. 🎁\n" +
      "👉 Venha conhecer a Assinatura de Banhos Petland e todos os benefícios!",
    consolidatedFragment: "• Conheça a Assinatura de Banhos Petland para manter a rotina de cuidados com economia e benefícios exclusivos.",
  },
};

const CRM_PET_NON_GENERAL_DEFAULT_MESSAGES = {
  [CRM_PET_RULES.NO_PRODUCT]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\nQue tal renovar os produtos de {nome_pet}? Estamos por aqui para ajudar a escolher tudo o que essa fofura precisa. 🐾🧡",
    consolidatedFragment: "• Podemos ajudar a renovar os produtos de {nome_pet}.",
  },
};

function getDefaultRuleMessage(ruleCode, subtypeCode, field) {
  const defaults = subtypeCode !== "GENERAL"
    ? CRM_PET_NON_GENERAL_DEFAULT_MESSAGES[ruleCode] || CRM_PET_DEFAULT_MESSAGES[ruleCode]
    : CRM_PET_DEFAULT_MESSAGES[ruleCode];
  return defaults?.[field] || "";
}

function getConfiguredRuleMessage(ruleCode, subtype, field) {
  const configuredTemplate = subtype[field];
  if (ruleCode === CRM_PET_RULES.ABSENT) return configuredTemplate || "";

  const isLegacyNoAdditionalTemplate = ruleCode === CRM_PET_RULES.NO_ADDITIONAL
    && field === "individualTemplate"
    && configuredTemplate?.trim() === LEGACY_NO_ADDITIONAL_INDIVIDUAL_TEMPLATE;

  if (configuredTemplate?.trim() && !isLegacyNoAdditionalTemplate) {
    return configuredTemplate;
  }

  return getDefaultRuleMessage(ruleCode, subtype.subtypeCode, field);
}

const RULE_FALLBACK_LABELS = {
  [CRM_PET_RULES.ABSENT]: "Ausente",
  [CRM_PET_RULES.NO_PRODUCT]: "Sem produto",
  [CRM_PET_RULES.NO_ADDITIONAL]: "Sem adicional",
  [CRM_PET_RULES.POTENTIAL_SUBSCRIBER]: "Potencial assinante",
};

export function getRuleLabel(ruleCode) {
  return RULE_FALLBACK_LABELS[ruleCode] || ruleCode;
}

function asString(value) {
  return value == null ? "" : String(value);
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

export function getMembershipKey(membership) {
  return `${membership.ruleCode}|${membership.subtypeCode}`;
}

export function getConfigLabel(config, ruleCode, subtypeCode) {
  const rule = (config?.rules || []).find((item) => item.ruleCode === ruleCode);
  const subtype = (rule?.subtypes || []).find((item) => item.subtypeCode === subtypeCode);
  return subtype?.label || RULE_FALLBACK_LABELS[ruleCode] || subtypeCode || ruleCode;
}

export function formatEvidence(membership = {}) {
  const evidence = membership.evidence || {};
  const windowDays = evidence.windowDays;
  const lastVisit = evidence.lastValidVisitAt;
  const visitText = lastVisit && dayjs(lastVisit).isValid()
    ? `Última visita em ${dayjs(lastVisit).format("DD/MM/YYYY")}`
    : null;

  if (membership.ruleCode === CRM_PET_RULES.ABSENT) {
    const days = lastVisit && dayjs(lastVisit).isValid()
      ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
      : null;
    return [days != null ? `${days} dias sem visita` : `Sem visita há ${windowDays || 30} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.NO_PRODUCT) {
    return [`Sem compra correspondente em ${windowDays || 90} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.NO_ADDITIONAL) {
    return [`Sem adicional correspondente em ${windowDays || 90} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.POTENTIAL_SUBSCRIBER) {
    const planCount = evidence.compatiblePlanCodes?.length || 0;
    return planCount ? `${planCount} plano(s) compatível(is)` : "Pet elegível para assinatura";
  }
  return "Oportunidade ativa";
}

export function formatCrmPetConfig(payload = {}) {
  return {
    ...payload,
    faixas: Array.isArray(payload.faixas) ? payload.faixas : [],
    rules: (payload.rules || []).map((rule) => ({
      ...rule,
      active: rule.active === true,
      windowDays: Number(rule.windowDays || 0),
      subtypes: (rule.subtypes || []).map((subtype) => ({
        ...subtype,
        active: true,
        individualTemplate: getConfiguredRuleMessage(rule.ruleCode, subtype, "individualTemplate"),
        consolidatedFragment: getConfiguredRuleMessage(rule.ruleCode, subtype, "consolidatedFragment"),
        sourceRefs: {
          ...subtype.sourceRefs,
          productSubcategoryIds: subtype.sourceRefs?.productSubcategoryIds || [],
          serviceCategoryIds: subtype.sourceRefs?.serviceCategoryIds || [],
          serviceIds: subtype.sourceRefs?.serviceIds || [],
        },
      })),
    })),
  };
}

export function hasCompleteCrmPetMessages(config = {}) {
  return (config.rules || []).every((rule) => (
    rule.ruleCode === CRM_PET_RULES.ABSENT || !rule.subtypes?.length || rule.subtypes.every((subtype) => (
      Boolean(subtype.individualTemplate?.trim() && subtype.consolidatedFragment?.trim())
    ))
  ));
}

export function prepareCrmPetConfigPayload(config = {}) {
  return {
    faixas: config.faixas || [],
    rules: (config.rules || []).map((rule) => ({
      ...rule,
      subtypes: (rule.subtypes || []).map((subtype) => ({
        ...subtype,
        active: true,
      })),
    })),
  };
}

export function formatCustomerContactSummary(memberships = []) {
  const contactedMemberships = memberships.filter((membership) => membership.contactCount > 0);
  if (!contactedMemberships.length) {
    return { status: "PENDING", lastContactAt: null };
  }

  const lastContactAt = contactedMemberships.reduce((latest, membership) => {
    const current = membership.lastContactAt;
    if (!current || !dayjs(current).isValid()) return latest;
    if (!latest || dayjs(current).isAfter(dayjs(latest))) return current;
    return latest;
  }, null);

  return { status: "CONTACTED", lastContactAt };
}

export function applyCrmPetContactEvent(row, event) {
  const petId = asString(row.pet?.petId);
  if (asString(row.customerId) !== asString(event.customerId)
    || !(event.petIds || []).map(asString).includes(petId)
    || !event.contactedAt || !dayjs(event.contactedAt).isValid()) return row;

  const covers = (membership) => {
    const membershipId = asString(membership.membershipId);
    if (!(event.membershipIds || []).map(asString).includes(membershipId)) return false;
    if (!event.selections?.length) return true;
    return event.selections.some((selection) => (
      asString(selection.membershipId) === membershipId
      && (Array.isArray(selection.petIds)
        ? selection.petIds.map(asString).includes(petId)
        : event.petIds.length === 1)
    ));
  };
  if (!row.memberships.some(covers)) return row;

  // Ausentes also creates a legacy contact, counted by the listing API.
  const hasLegacyContact = Boolean(event.legacyContactIds?.length)
    && (event.selections || []).some((selection) => (
      selection.ruleCode === CRM_PET_RULES.ABSENT
      && (selection.petIds || []).map(asString).includes(petId)
    ));
  const memberships = row.memberships.map((membership) => {
    if (!covers(membership)) return membership;
    const lastContactAt = membership.lastContactAt
      && dayjs(membership.lastContactAt).isAfter(event.contactedAt)
      ? membership.lastContactAt : event.contactedAt;
    const expires = dayjs(lastContactAt).add(168, "hour");
    const active = expires.isAfter(dayjs());
    return {
      ...membership,
      contactCount: Number(membership.contactCount || 0) + 1
        + Number(hasLegacyContact && membership.ruleCode === CRM_PET_RULES.ABSENT),
      lastContactAt,
      contactStatus: active ? "CONTACTED" : "PENDING",
      contactExpiresAt: active ? expires.toISOString() : null,
    };
  });
  const allContacted = memberships.every((membership) => membership.contactStatus === "CONTACTED");
  const anyContacted = memberships.some((membership) => membership.contactStatus === "CONTACTED");
  const previous = row.contactSummary || {};
  return {
    ...row,
    memberships,
    contactSummary: {
      ...previous,
      status: allContacted ? "CONTACTED" : "PENDING",
      partial: anyContacted && !allContacted,
      contactCount: Number(previous.contactCount || 0) + 1 + Number(hasLegacyContact),
      latestContactAt: previous.latestContactAt && dayjs(previous.latestContactAt).isAfter(event.contactedAt)
        ? previous.latestContactAt : event.contactedAt,
    },
  };
}

export function formatOpportunitiesResponse(payload = {}) {
  const customers = (payload.results || []).map((row) => {
    const memberships = (row.memberships || []).map((membership) => ({
      ...membership,
      membershipId: asString(membership.membershipId || membership._id),
      petIds: (membership.petIds || []).map(asString),
      contactCount: Number(membership.contactCount || 0),
      ruleLabel: getRuleLabel(membership.ruleCode),
      label: membership.label || RULE_FALLBACK_LABELS[membership.ruleCode] || membership.subtypeCode,
      evidenceText: formatEvidence(membership),
    }));
    const pet = {
      ...(row.pet || {}),
      petSex: normalizePetSex(row.pet?.petSex),
      petId: asString(row.pet?.petId),
      blocked: row.pet?.blocked === true || row.pet?.bloqueado === true,
      blockedAt: row.pet?.blockedAt || row.pet?.dataBloqueio || null,
    };
    const rowKey = row.rowKey || `${row.customerId || row.customerCpf}:${pet.petId}`;

    return {
      ...row,
      rowKey,
      customerKey: rowKey,
      pet,
      pets: [pet],
      memberships,
      subscription: {
        status: row.subscription?.status || "NEVER",
        frequencyPerMonth: Number(row.subscription?.frequencyPerMonth || 0),
        lastSubscriptionAt: row.subscription?.lastSubscriptionAt || null,
      },
      activity: {
        absenceDays: row.activity?.absenceDays ?? null,
        lastAppointmentAt: row.activity?.lastAppointmentAt || null,
        nextAppointmentAt: row.activity?.nextAppointmentAt || null,
      },
      categoryActivity: row.categoryActivity || {},
      contactSummary: {
        ...row.contactSummary,
        status: row.contactSummary?.status || "PENDING",
        contactCount: Number(row.contactSummary?.contactCount || 0),
        latestContactAt: row.contactSummary?.latestContactAt || null,
      },
    };
  });
  const pagination = payload.pagination || {};
  const summary = payload.summary || {};

  return {
    customers,
    pagination: {
      page: Math.max(0, Number(pagination.currentPage || 1) - 1),
      perPage: Number(pagination.perPage || 10),
      totalDocuments: Number(pagination.total || 0),
      totalPages: Number(pagination.totalPages || 1),
    },
    summary: {
      totalAll: Number(summary.totalPets || 0),
      realizados: Number(summary.contactedPets || 0),
      pendentes: Number(summary.pendingPets || 0),
      bloqueados: Number(summary.blockedPets || 0),
    },
  };
}

export function formatContactHistory(payload, config) {
  const items = Array.isArray(payload) ? payload : payload?.results || [];
  return items.map((item) => {
    const selections = item.selections?.length
      ? item.selections
      : (item.rules || []).map((ruleCode, index) => ({
        ruleCode,
        subtypeCode: item.subtypes?.[index] || "GENERAL",
      }));
    const pets = item.pets?.length
      ? item.pets
      : item.petId ? [{ petId: item.petId, petName: item.petName }] : [];
    return {
      ...item,
      contactedAt: item.contactedAt || item.dataContato || item.createdAt,
      responsibleName: item.responsibleName || item.responsavelNome || "-",
      message: item.message || item.observacao || "",
      pets,
      selections: selections.map((selection) => ({
        ...selection,
        ruleLabel: getRuleLabel(selection.ruleCode),
        label: getConfigLabel(config, selection.ruleCode, selection.subtypeCode),
      })),
      legacy: item.legacy === true,
    };
  });
}

function contactFirstName(name) {
  return normalizeName(name).split(/\s+/)[0] || "cliente";
}

function replaceVariables(template, customer, pets, membership) {
  const selectedPets = pets || [];
  const petNames = selectedPets.map((pet) => normalizeName(pet.petName) || pet.petName).filter(Boolean);
  const petReference = crmPetReference(selectedPets);
  const lastVisit = membership?.evidence?.lastValidVisitAt;
  const absenceDays = lastVisit && dayjs(lastVisit).isValid()
    ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
    : membership?.evidence?.windowDays || "";
  const values = {
    nome_cliente: contactFirstName(customer.customerName),
    nome_pet: petReference,
    nome_pets: petNames.join(" e ") || petReference,
    artigo_pet: crmPetArticle(selectedPets),
    dias_ausente: absenceDays,
    nome_loja: "",
    telefone_loja: "",
  };
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replace(new RegExp(`\\{${key}\\}`, "g"), asString(value)),
    template || "",
  ).replace(/\bda a (duplinha|turma)\b/g, "da $1");
}

function getRuleMessageConfig(config, ruleCode) {
  const rule = (config?.rules || []).find((item) => item.ruleCode === ruleCode);
  return (rule?.subtypes || []).find((subtype) => subtype.subtypeCode === "GENERAL")
    || rule?.subtypes?.[0]
    || null;
}

function getRuleMessageTemplate(config, membership, field) {
  const configuredTemplate = getRuleMessageConfig(config, membership.ruleCode)?.[field];
  if (configuredTemplate?.trim()) return configuredTemplate;
  return CRM_PET_DEFAULT_MESSAGES[membership.ruleCode]?.[field] || "";
}

function buildAbsenceMessage(customer, pets, membership, config) {
  const lastVisit = membership.evidence?.lastValidVisitAt;
  const absenceDays = lastVisit && dayjs(lastVisit).isValid()
    ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
    : membership.evidence?.windowDays || 30;

  return resolveMessage(
    config?.faixas || [],
    absenceDays,
    contactFirstName(customer.customerName),
    crmPetReference(pets),
    "",
    crmPetArticle(pets),
  ).replace(/\bda a (duplinha|turma)\b/g, "da $1");
}

export function buildContactMessage({ customer, memberships, pets, config }) {
  if (!memberships?.length || !pets?.length) return "";
  const selectedRuleCodes = unique(memberships.map((membership) => membership.ruleCode));
  if (selectedRuleCodes.length === 1) {
    const original = mergeMembershipsForMessage(memberships);
    const relatedPets = pets.filter((pet) => (original.petIds || []).includes(pet.petId));
    if (!relatedPets.length) return "";
    const membership = membershipForPets(original, relatedPets);
    pets = relatedPets;
    if (membership.ruleCode === CRM_PET_RULES.ABSENT) {
      return buildRelatedAbsenceMessage(customer, pets, membership, config);
    }

    const individualTemplate = getRuleMessageTemplate(config, membership, "individualTemplate");
    if (individualTemplate) {
      return replaceVariables(individualTemplate, customer, pets, membership);
    }

    return replaceVariables(
      `Oi, {nome_cliente}! 😊\n\nIdentificamos uma oportunidade para {nome_pets}: ${membership.label}. Posso ajudar? 🐾🧡`,
      customer,
      pets,
      membership,
    );
  }

  const absenceMemberships = memberships.filter((membership) => (
    membership.ruleCode === CRM_PET_RULES.ABSENT
  ));
  const consolidatedMemberships = memberships.filter((membership) => (
    membership.ruleCode !== CRM_PET_RULES.ABSENT
  ));
  const fragments = unique(consolidatedMemberships.map((membership) => {
    const relatedPets = pets.filter((pet) => (membership.petIds || []).includes(pet.petId));
    if (!relatedPets.length) return "";
    return replaceVariables(
      getRuleMessageTemplate(config, membership, "consolidatedFragment") || `• ${membership.label}`,
      customer, relatedPets, membershipForPets(membership, relatedPets),
    );
  }));

  if (absenceMemberships.length) {
    const absenceMessage = unique(absenceMemberships.map((membership) => {
      const related = pets.filter((pet) => (membership.petIds || []).includes(pet.petId));
      return related.length ? buildRelatedAbsenceMessage(customer, related, membership, config) : "";
    })).join("\n\n");
    return fragments.length ? `${absenceMessage}\n\n${fragments.join("\n")}` : absenceMessage;
  }

  const greeting = replaceVariables("Oi, {nome_cliente}! 😊", customer, pets);
  return `${greeting}\n\n${fragments.join("\n")}\n\nPosso ajudar com essas oportunidades para ${crmPetReference(pets)}? 🐾🧡`;
}

export function buildRuleContactMessage({ customer, ruleCode, memberships, pets, config }) {
  if (!ruleCode || !pets?.length) return "";
  const ruleMemberships = (memberships || []).filter((membership) => membership.ruleCode === ruleCode);
  if (!ruleMemberships.length) return "";

  const petIds = pets.map((pet) => pet.petId);
  const petEvidence = pets.reduce((result, pet) => {
    const membership = ruleMemberships.find((item) => (
      item.petEvidence?.[pet.petId]
      || ((item.petIds || []).includes(pet.petId) && item.evidence)
    ));
    const membershipEvidence = membership?.petEvidence?.[pet.petId]
      || membership?.evidence
      || {};
    const lastAppointmentAt = pet.activity?.lastAppointmentAt || pet.lastAppointmentAt;
    const absenceDays = pet.activity?.absenceDays ?? pet.absenceDays;

    result[pet.petId] = ruleCode === CRM_PET_RULES.ABSENT
      ? {
        ...membershipEvidence,
        lastValidVisitAt: membershipEvidence.lastValidVisitAt || lastAppointmentAt || null,
        windowDays: membershipEvidence.windowDays ?? absenceDays ?? 30,
      }
      : membershipEvidence;
    return result;
  }, {});

  const projectedMemberships = ruleMemberships.map((membership) => ({
    ...membership,
    petIds,
    petEvidence: {
      ...(membership.petEvidence || {}),
      ...petEvidence,
    },
  }));

  return buildContactMessage({ customer, memberships: projectedMemberships, pets, config });
}

export function isMembershipCovered(membership, selectedPetIds) {
  return (membership.petIds || []).some((petId) => selectedPetIds.includes(petId));
}

export function normalizePetSex(value) {
  const sex = String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
  if (["f", "femea", "female"].includes(sex)) return "female";
  if (["m", "macho", "male"].includes(sex)) return "male";
  return null;
}

function crmPetReference(pets) {
  if (pets.length === 1 && !normalizePetSex(pets[0].petSex)) {
    return `seu pet ${normalizeName(pets[0].petName) || ""}`.trim();
  }
  return buildPetRef(pets);
}

function crmPetArticle(pets) {
  return pets.length !== 1 || normalizePetSex(pets[0].petSex) === "female" ? "da" : "do";
}

function membershipForPets(membership, pets) {
  return pets.length === 1 && membership.petEvidence?.[pets[0].petId]
    ? { ...membership, evidence: membership.petEvidence[pets[0].petId] }
    : membership;
}

function mergeMembershipsForMessage(memberships) {
  const petEvidence = memberships.reduce((result, membership) => {
    Object.assign(result, membership.petEvidence || {});
    if (membership.petIds?.length === 1 && membership.evidence) {
      result[membership.petIds[0]] ||= membership.evidence;
    }
    return result;
  }, {});

  return {
    ...memberships[0],
    petIds: unique(memberships.flatMap((membership) => membership.petIds || [])),
    ...(Object.keys(petEvidence).length ? { petEvidence } : {}),
  };
}

function buildRelatedAbsenceMessage(customer, pets, membership, config) {
  if (pets.length > 1 && membership.petEvidence) {
    return pets.map((pet) => buildAbsenceMessage(customer, [pet], membershipForPets(membership, [pet]), config)).join("\n\n");
  }
  return buildAbsenceMessage(customer, pets, membershipForPets(membership, pets), config);
}

export function formatCustomerOpportunities(payload) {
  return {
    ...payload,
    pets: (payload.pets || []).map((pet) => ({ ...pet, petId: asString(pet.petId), petSex: normalizePetSex(pet.petSex) })),
    memberships: (payload.memberships || []).map((membership) => ({
      ...membership,
      membershipId: asString(membership.membershipId),
      petIds: (membership.petIds || []).map(asString),
    })),
  };
}
